import { useCallback, useMemo, useRef } from 'react';
import noop from '@picsart/rc/noop';
import { isSafari } from 'react-device-detect';

import useMouseMoveOnce from 'hooks/useMouseMoveOnce';

import { minSize, origins } from './constants';
import { IBadgeHandlerParams, ICropSides } from './types';

const { PI, sin, atan2, abs } = Math;
const safariCoefficient = isSafari ? 6 : 1;

const useChangeHandler = ({ objectWidth = 1, objectHeight = 1, rotationRadian = 0, onChangeBorder = noop }) => {
  const lastTouch = useRef<{ x: number; y: number } | null>(null);

  const { cosAngle, sinAngle } = useMemo(
    () => ({
      cosAngle: sin(PI / 2 - rotationRadian),
      sinAngle: sin(rotationRadian),
    }),
    [rotationRadian],
  );

  const getMovement = useCallback((event: MouseEvent | TouchEvent) => {
    let movementX = 0;
    let movementY = 0;

    if (event instanceof MouseEvent) {
      movementX = event.movementX;
      movementY = event.movementY;
    } else if (event instanceof TouchEvent && event.changedTouches && event.changedTouches.length) {
      const touch = event.changedTouches[0];

      // If last touch positions are set, compute movement
      if (lastTouch.current) {
        movementX = touch.clientX - lastTouch.current.x;
        movementY = touch.clientY - lastTouch.current.y;
      }

      // Update the last touch positions
      lastTouch.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
    }
    return { movementX, movementY };
  }, []);

  const rectChangeHandler = useCallback(
    (mouseMoveEvent: MouseEvent | TouchEvent, leftRightRange: number, topBottomRange: number) => {
      // calculate movement for each axis
      mouseMoveEvent.preventDefault();
      mouseMoveEvent.stopImmediatePropagation();
      const { movementX, movementY } = getMovement(mouseMoveEvent);
      const xChange = (cosAngle * movementX + sinAngle * movementY) * safariCoefficient;
      const yChange = (cosAngle * movementY - sinAngle * movementX) * safariCoefficient;

      const updateBorder = (prevBorder: ICropSides) => {
        const topChange = prevBorder.top + yChange;
        const leftChange = prevBorder.left + xChange;
        const isLeftAcceptable = leftChange >= 0;
        const isLeftRightInRange = leftChange <= leftRightRange;
        const isTopAcceptable = topChange >= 0;
        const isTopBottomInRange = topChange <= topBottomRange;
        const left = isLeftRightInRange ? (isLeftAcceptable ? leftChange : 0) : prevBorder.left;
        const top = isTopBottomInRange ? (isTopAcceptable ? topChange : 0) : prevBorder.top;
        const right = leftRightRange - left >= 0 ? leftRightRange - left : 0;
        const bottom = topBottomRange - top >= 0 ? topBottomRange - top : 0;

        return { left, right, top, bottom };
      };

      onChangeBorder(updateBorder);
    },
    [getMovement, cosAngle, sinAngle, onChangeBorder],
  );

  const badgeChangeHandler = useCallback(
    (mouseMoveEvent: MouseEvent | TouchEvent, { origin, ratio, ranges, respectRatio, scales: [scaleXNeeded, scaleYNeeded] }: IBadgeHandlerParams) => {
      // this section is comparing movement angle with direction, positive/negative angles range
      mouseMoveEvent.preventDefault();
      mouseMoveEvent.stopImmediatePropagation();
      const { movementX, movementY } = getMovement(mouseMoveEvent);
      // adding PI to movementAngle to work with positive angle, original range is (-PI, PI)
      const movementAngle = atan2(movementY, movementX) + PI;
      const isAngleInPositiveRange = ranges.some(([min, max]) => movementAngle >= min && movementAngle <= max);

      const coefficient = isAngleInPositiveRange ? -1 : 1;
      // calculate changes for each axis
      const moveX = abs(movementX) * safariCoefficient;
      const moveY = abs(movementY) * safariCoefficient;

      const xChange = abs(moveX * cosAngle) + abs(moveY * sinAngle);
      const yChange = respectRatio ? xChange * ratio : abs(moveY * cosAngle) + abs(moveX * sinAngle);

      const updateBorder = ({ left, right, top, bottom }: ICropSides) => {
        const multipliedXChange = coefficient * xChange;
        const multipliedYChange = coefficient * yChange;

        const isLeftAcceptable = multipliedXChange + left >= 0;
        const isRightAcceptable = multipliedXChange + right >= 0;
        const isTopAcceptable = multipliedYChange + top >= 0;
        const isBottomAcceptable = multipliedYChange + bottom >= 0;

        // check whether the corresponding change is needed
        const leftChangeNeeded = origin.includes(origins.left);
        const rightChangeNeeded = origin.includes(origins.right);
        const topChangeNeeded = origin.includes(origins.top);
        const bottomChangeNeeded = origin.includes(origins.bottom);

        // checking the both borders acceptance if the transformation needs to respect the ratio
        const isLeftRightRatioAcceptable = !respectRatio || ((!leftChangeNeeded || isLeftAcceptable) && (!rightChangeNeeded || isRightAcceptable));
        const isTopBottomRatioAcceptable = !respectRatio || ((!topChangeNeeded || isTopAcceptable) && (!bottomChangeNeeded || isBottomAcceptable));
        const isRatioRespected = isLeftRightRatioAcceptable && isTopBottomRatioAcceptable;

        // check borders sum to be not more than original sizes
        const borderedSizeTopBottom = 2 * multipliedYChange + top + bottom;
        const borderedSizeLeftRight = 2 * multipliedXChange + left + right;
        const isLeftRightInRange = borderedSizeLeftRight <= objectWidth - minSize;
        const isTopBottomInRange = borderedSizeTopBottom <= objectHeight - minSize;

        const isInRangeWhenRatio = isLeftRightInRange && isTopBottomInRange;
        const getRatio = (theRange: boolean) => (isRatioRespected ? isInRangeWhenRatio : theRange);

        return {
          left: isLeftAcceptable ? (leftChangeNeeded && getRatio(isLeftRightInRange) ? scaleXNeeded * multipliedXChange + left : left) : 0,
          right: isRightAcceptable ? (rightChangeNeeded && getRatio(isLeftRightInRange) ? scaleXNeeded * multipliedXChange + right : right) : 0,
          top: isTopAcceptable ? (topChangeNeeded && getRatio(isTopBottomInRange) ? scaleYNeeded * multipliedYChange + top : top) : 0,
          bottom: isBottomAcceptable ? (bottomChangeNeeded && getRatio(isTopBottomInRange) ? scaleYNeeded * multipliedYChange + bottom : bottom) : 0,
        };
      };

      onChangeBorder(updateBorder);
    },
    [getMovement, cosAngle, sinAngle, onChangeBorder, objectWidth, objectHeight],
  );

  const clearLastTouchPosition = useCallback(() => {
    lastTouch.current = null;
  }, []);

  const rectMoveHandler = useMouseMoveOnce(rectChangeHandler);
  const badgeMoveHandler = useMouseMoveOnce(badgeChangeHandler);

  return {
    rectMoveHandler, badgeMoveHandler, clearLastTouchPosition,
  };
};

export default useChangeHandler;
